import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, mediaUrl } from "@/lib/api";
import { spaceName } from "@/lib/format";
import { VisibilityPicker } from "./VisibilityIcon";
import { NameChips } from "./NameChips";
import { waitForJob } from "./MoveDialog";

const coverId = (a) => a?.cover_url?.match(/media\/([^/]+)\//)?.[1] || null;
const label = (s) => (s === "private" || s?.startsWith("user:") ? "private" : s);

const Choice = ({ checked, onChange, children, testid }) => (
  <label className="flex gap-2 items-center text-sm text-neutral-700"><input type="radio" checked={checked} onChange={onChange} data-testid={testid} />{children}</label>
);

// Merge this (source) album into another regular album
export const MergeDialog = ({ open, onClose, source }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [target, setTarget] = useState(null);
  const [nameMode, setNameMode] = useState("target");
  const [custom, setCustom] = useState("");
  const [cover, setCover] = useState("target");
  const [vis, setVis] = useState("private");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTarget(null); setNameMode("target"); setCustom(""); setCover("target"); setBusy(false);
    api.get("/albums", { params: { space: "all", kind: "regular", limit: 200 } }).then((r) => setAlbums(r.data.albums.filter((a) =>
      a.id !== source.id && !a.unsorted && !a.smart && !a.yearbook && (user?.role === "admin" || a.owner_id === user?.user_id))));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickTarget = (id) => { const t = albums.find((a) => a.id === id); setTarget(t || null); if (t) setVis(label(t.space)); };
  const submit = async (e) => {
    e.preventDefault();
    const name = nameMode === "custom" ? custom : nameMode === "source" ? source.name : target.name;
    const body = { source_id: source.id, name, cover_item_id: coverId(cover === "source" ? source : target), target: vis };
    setBusy(true);
    try {
      const { data: p } = await api.post(`/albums/${target.id}/merge/preview`, body);
      const move = p.move > 0 && window.confirm(`Move ${p.move} photo${p.move === 1 ? "" : "s"} to ${spaceName(vis)} too?${p.stay ? ` ${p.stay} owned by others will stay where they are.` : ""}\n(Cancel leaves them out of the merged album.)`);
      const { data } = await api.post(`/albums/${target.id}/merge`, { ...body, move });
      if (data.job_id) {
        const id = toast.loading(`Moving 0 of ${data.total}…`);
        await waitForJob(data.job_id, (j) => toast.loading(`Moving ${j.pos} of ${j.total}…`, { id }));
        toast.dismiss(id);
      }
      toast.success(`Merged into “${data.album.name}” · ${data.album.item_count} photos${data.left_out ? ` · ${data.left_out} left out` : ""}`);
      onClose();
      navigate(`/albums/${target.id}`, { replace: true });
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md bg-[#f4f3f0]" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Merge “{source.name}” into…</DialogTitle>
          <DialogDescription>All its photos join the target (no duplicates); this album is then removed and its share link stops working.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <select className="login-input !mb-0" required value={target?.id || ""} onChange={(e) => pickTarget(e.target.value)} data-testid="merge-target-select">
            <option value="">Choose the album to merge into…</option>
            {albums.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.item_count})</option>)}
          </select>
          {target && (
            <>
              <div className="flex flex-col gap-1.5" data-testid="merge-name-options">
                <Choice checked={nameMode === "target"} onChange={() => setNameMode("target")} testid="merge-name-target">Keep “{target.name}”</Choice>
                <Choice checked={nameMode === "source"} onChange={() => setNameMode("source")} testid="merge-name-source">Use “{source.name}”</Choice>
                <Choice checked={nameMode === "custom"} onChange={() => setNameMode("custom")} testid="merge-name-custom">New name</Choice>
                {nameMode === "custom" && (
                  <>
                    <input className="login-input !mb-0" required value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Album name" data-testid="merge-name-input" />
                    <NameChips albumIds={[source.id, target.id]} onPick={setCustom} testid="merge-name-chips" />
                  </>
                )}
              </div>
              <div className="flex gap-3" data-testid="merge-cover-options">
                {[["target", target], ["source", source]].map(([k, a]) => (
                  <button type="button" key={k} onClick={() => setCover(k)} className={`merge-cover ${cover === k ? "on" : ""}`} data-testid={`merge-cover-${k}`}>
                    {a.cover_url ? <img src={mediaUrl(a.cover_url)} alt="" /> : <span />}<small>{k === "target" ? "Target cover" : "This cover"}</small>
                  </button>
                ))}
              </div>
              <VisibilityPicker value={vis} onChange={setVis} testid="merge-visibility" />
            </>
          )}
          <button className="btn-ink" disabled={!target || busy} data-testid="merge-submit">{busy ? "Merging…" : "Merge albums"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
